import { CircularProgress, LinearProgress } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import DragAndDropTable from "components/Modules/DragAndDropTable";
import DeleteDialog from "components/Elements/DeleteDialog";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../../redux/common/actions";
import { Apis } from "services/api";
import ActionButtonStyle from "styles/application/ActionButtonStyle";
import AddOrEditPermit from "./AddEditPermit";
import {
	addModelTaskPermit,
	patchModelTaskPermit,
	repositionModelTaskPermit,
} from "services/models/modelDetails/modelTaskPermits";
import withMount from "components/HOC/withMount";
import GeneralButton from "components/Elements/GeneralButton";
import { pasteModelTaskPermit } from "services/models/modelDetails/modelTasks/pasteApi";
import {
	EDIT_ACCESS,
	FULL_ACCESS,
	READONLY_ACCESS,
} from "constants/AccessTypes/AccessTypes";
import useModelTaskPermits from "hooks/ModuleTaskTableHooks/useModelTaskPermits";
import { getLocalStorageData } from "helpers/utils";

const AT = ActionButtonStyle();

const Permits = ({ taskInfo, access, isMounted }) => {
	const [selectedID, setSelectedID] = useState(null);
	const [openAddPermit, setOpenAddPermit] = useState(false);
	const [openEditPermit, setOpenEditPermit] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [pastePart, setPastePart] = useState(true);
	const [isPasting, setIsPasting] = useState(false);

	const { customCaptions } = getLocalStorageData("me");
	const dispatch = useDispatch();
	// hook for tools page
	const {
		permits,
		loading,
		setPermits,
		setOriginalPermits,
		originalPermits,
		fetchPermits,
		CtxDispatch,
		state,
		MtxDispatch,
	} = useModelTaskPermits({ taskInfo, isMounted });

	useEffect(() => {
		setPastePart(state.isPermitsTaskDisabled);
	}, [state]);

	// handle dragging of permit

	const handleDragEnd = async (e) => {
		if (!e.destination) {
			return;
		}

		if (e.destination.index === e.source.index) return;

		const result = [...permits];
		const [removed] = result.splice(e.source.index, 1);
		result.splice(e.destination.index, 0, removed);
		setPermits(result);

		try {
			let payloadBody = {
				id: e.draggableId,
				destinationId: e?.destination?.destinationId,
				direction: e?.destination?.direction,
			};
			const response = await repositionModelTaskPermit(
				e.draggableId,
				payloadBody
			);
			if (response.status) {
				const newDate = result.map((x, i) =>
					i === e.destination.index ? { ...x, pos: response.data.pos } : x
				);
				setOriginalPermits(newDate);
				setPermits(newDate);
			} else {
				setPermits(originalPermits);
			}
		} catch (error) {
			setPermits(originalPermits);
		}
	};

	// show popup for deletion of permit
	const showDeletePermitPopUp = (TaskPartID) => {
		setOpenDeleteDialog(true);
		setSelectedID(TaskPartID);
	};

	// remove permit from permit list
	const handleRemoveData = (id) => {
		const newData = [...permits].filter(function (item) {
			return item.id !== id;
		});
		setPermits(newData);
		setOriginalPermits(newData);
		CtxDispatch({
			type: "TAB_COUNT",
			payload: {
				countTab: "permitCount",
				data: newData.length,
			},
		});
		if (newData.length === 0) {
			CtxDispatch({
				type: "SET_PERMITS",
				payload: false,
			});
		}
	};

	const createPermit = async (newPermit) => {
		return await addModelTaskPermit({
			ModelVersionTaskID: taskInfo.id,
			...newPermit,
		});
	};

	// show popUp for edit of permit
	const showEditPermitPopUp = (row) => {
		row = permits.find((r) => r.id === row);
		setOpenEditPermit(true);
		setSelectedID(row);
	};

	const editPermit = async (payload) => {
		return await patchModelTaskPermit(selectedID.id, [
			{ op: "replace", path: "name", value: payload.name },
		]);
	};

	const handleCopy = (id) => {
		localStorage.setItem("taskpermit", id);
		MtxDispatch({ type: "DISABLE_PERMIT_TASK", payload: false });
	};

	const handlePaste = async () => {
		setIsPasting(true);
		try {
			const taskPartId = localStorage.getItem("taskpermit");
			let result = await pasteModelTaskPermit(taskInfo.id, {
				modelVersionTaskPermitID: taskPartId,
			});

			if (!isMounted.aborted) {
				if (result.status) {
					await fetchPermits(false);
				} else {
					// errorResponse(result);
					dispatch(showError(result?.data?.detail || "Could not paste"));
				}
			}
		} catch (e) {
			return;
		}
		setIsPasting(false);
	};

	const checkcopyPartStatus = async () => {
		try {
			const taskId = localStorage.getItem("taskpermit");

			if (taskId) {
				setPastePart(false);
			}
		} catch (error) {
			return;
		}
	};

	const visibilitychangeCheck = function () {
		if (!document.hidden) {
			checkcopyPartStatus();
		}
	};

	useEffect(() => {
		checkcopyPartStatus();
		document.addEventListener("visibilitychange", visibilitychangeCheck);
		return () =>
			document.removeEventListener("visibilitychange", visibilitychangeCheck);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (loading) return <CircularProgress />;
	return (
		<div style={{ marginBottom: "20px" }}>
			<AddOrEditPermit
				open={openAddPermit}
				title={`Add ${customCaptions.permit}`}
				closeHandler={() => {
					setOpenAddPermit(false);
				}}
				data={null}
				createProcessHandler={createPermit}
				fetchData={() => fetchPermits(false)}
			/>
			<AddOrEditPermit
				open={openEditPermit}
				title={`${customCaptions.permit}`}
				closeHandler={() => {
					setOpenEditPermit(false);
				}}
				data={{
					name: selectedID?.name,
				}}
				createProcessHandler={editPermit}
				fetchData={() => fetchPermits(false)}
				isEdit
			/>
			<DeleteDialog
				entityName={`${customCaptions.permit}`}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteEndpoint={Apis.ModelVersionTaskPermits}
				deleteID={selectedID}
				handleRemoveData={handleRemoveData}
			/>
			<div
				className="detailsContainer topContainerCustomCaptions"
				style={{ alignItems: "center" }}
			>
				<DetailsPanel
					header={`${customCaptions.permitPlural}`}
					dataCount={permits.length}
				/>
				{access === FULL_ACCESS && !state.modelDetail?.isPublished && (
					<>
						<GeneralButton
							style={{ background: "#ED8738" }}
							onClick={handlePaste}
							disabled={pastePart}
						>
							Paste {`${customCaptions.permit}`}
						</GeneralButton>
						<AT.GeneralButton
							onClick={() => {
								setOpenAddPermit(true);
							}}
						>
							Add {customCaptions.permit}
						</AT.GeneralButton>
					</>
				)}
			</div>
			{isPasting ? <LinearProgress /> : null}

			<DragAndDropTable
				data={permits}
				headers={["Name"]}
				columns={[{ id: 2, name: "name", style: { width: "100vw" } }]}
				handleDragEnd={handleDragEnd}
				menuData={[
					{
						name: "Edit",
						handler: showEditPermitPopUp,
						isDelete: false,
					},
					{
						name: "Copy",
						handler: handleCopy,
					},
					{
						name: "Remove",
						handler: showDeletePermitPopUp,
						isDelete: true,
					},
				].filter((x) => {
					if (state.modelDetail?.isPublished) {
						return x?.name === "Copy";
					}

					if (access === FULL_ACCESS) return true;
					if (access === EDIT_ACCESS) {
						if (x.name === "Edit") return true;
						else return false;
					}
					return false;
				})}
				isModelEditable
				disableDnd={
					access === READONLY_ACCESS || state.modelDetail?.isPublished
				}
			/>
		</div>
	);
};

export default withMount(Permits);
